import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Divider, Fab, Grid, IconButton, Tab, Tooltip } from '@mui/material';
import { SyntheticEvent, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { PermissionDto, ResourceType } from '../../common/PricePrgClient';
import { StyledTabs } from '../../common/StyledComponents';
import { scrollableY } from '../../common/theme/scrollbar';
import { RootState } from '../../store';
import { getPermissions } from '../../utils';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import CreateExternalPricelistDialog from './CreateExternalPricelistDialog';
import ExternalPricelistList from './ExternalPricelistList';
import CustomersExternalPricelistTab from './Tabs/Customers/CustomersExternalPricelistTab';
import GeneralExternalPricelistTab from './Tabs/General/GeneralExternalPricelistTab';
import { ExternalPricelistTreeItem } from './externalPriceTypes';
import { getExternalPricelistTree } from './redux/externalPricelistSelectors';
import { openCreateExternalPricelistDialog, removeExternalPricelist } from './redux/externalPricelistSlices';

const mapStateToProps = (state: RootState) => ({
    externalPricelists: getExternalPricelistTree(state),
    selectedExternalPricelistId: state.externalPricelist.ui.selectedExternalPricelistId,
    currentUserPermissions: getCurrentUserPermissions(state),
    isCreateExternalPricelistDialogOpen: state.externalPricelist.ui.createExternalPricelistDialog.isOpen
});

type Props = ReturnType<typeof mapStateToProps>;

const ExternalPriceList = ({
    selectedExternalPricelistId,
    externalPricelists,
    currentUserPermissions,
    isCreateExternalPricelistDialogOpen
}: Props) => {
    const [hoveredExternalPricelistId, setHoveredExternalPricelistId] = useState(-1);
    const [tabIndex, setTabIndex] = useState(0);
    const dispatch = useDispatch();

    const handleCreateNewExternalPricelist = () => {
        dispatch(openCreateExternalPricelistDialog());
    };

    const handleDeleteExternalPricelist = (extPricelist: ExternalPricelistTreeItem) => {
        dispatch(
            showConfirmDialog({
                dialogTitle: `Delete ${extPricelist.displayName}`,
                dialogMessage: `Do you want to delete ${extPricelist.displayName}?`,
                primaryActionName: 'Delete',
                okAction: removeExternalPricelist.request(extPricelist.id)
            })
        );
    };

    const extPricelistPermissions: { [pricelistId: number]: PermissionDto } = useMemo(() => {
        const plPermObj: { [pricelistId: number]: PermissionDto } = {};

        const plPerms = getPermissions(
            ResourceType.ExternalPricelist,
            [0, ...Object.keys(externalPricelists).map(Number)],
            currentUserPermissions
        );

        for (const plPerm of plPerms) {
            plPermObj[plPerm.recordId!] = plPerm;
        }

        return plPermObj;
    }, [currentUserPermissions, externalPricelists]);

    const canCreateExternalPricelists = extPricelistPermissions[0]?.canCreate;

    const handleOnMouseOver = (externalPricelistId: number) => {
        if (externalPricelistId !== hoveredExternalPricelistId) {
            setHoveredExternalPricelistId(externalPricelistId);
        }
    };

    const handleTabChange = (event: SyntheticEvent<Element, Event>, newTabIndex: number) => {
        setTabIndex(newTabIndex);
    };

    const listItemRenderer = (item: ExternalPricelistTreeItem) => {
        return (
            <Box
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    maxHeight: '25px',
                    justifyContent: 'space-between'
                }}
                onMouseEnter={() => handleOnMouseOver(item.id)}
            >
                <Box component="span">{item.displayName}</Box>
                <Box sx={hoveredExternalPricelistId !== item.id ? { display: 'none' } : undefined}>
                    <Tooltip title={`Delete pricelist ${item.name}`}>
                        <IconButton
                            onClick={() => handleDeleteExternalPricelist(item)}
                            color="secondary"
                            disabled={
                                !(
                                    currentUserPermissions.isAdmin ||
                                    extPricelistPermissions[item.id]?.canDelete
                                )
                            }
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Grid container sx={{ flexFlow: 'row' }}>
                <Grid item xs={3} sx={theme => ({ ...scrollableY(theme) })}>
                    <ExternalPricelistList
                        selectedExternalPricelistIds={
                            selectedExternalPricelistId != null ? [selectedExternalPricelistId] : []
                        }
                        onSelectPricelist={null}
                        multiselect={false}
                        listItemRenderer={listItemRenderer}
                        header={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    paddingRight: '9px'
                                }}
                            >
                                <Tooltip title="Create new external pricelist">
                                    <Box component="span">
                                        <Fab
                                            size="small"
                                            color="primary"
                                            onClick={() => handleCreateNewExternalPricelist()}
                                            disabled={!canCreateExternalPricelists}
                                        >
                                            <AddIcon />
                                        </Fab>
                                    </Box>
                                </Tooltip>
                            </Box>
                        }
                    />
                </Grid>
                <Divider orientation="vertical" flexItem />
                <Grid item xs={9} sx={{ paddingLeft: '10px' }}>
                    <StyledTabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="General" />
                        <Tab label="Customers" />
                    </StyledTabs>

                    <Box p={2}>
                        {tabIndex === 0 && <GeneralExternalPricelistTab />}

                        {tabIndex === 1 && <CustomersExternalPricelistTab />}
                    </Box>
                </Grid>
            </Grid>

            {isCreateExternalPricelistDialogOpen && <CreateExternalPricelistDialog />}
        </>
    );
};

export default connect(mapStateToProps)(ExternalPriceList);
